import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, Loader2, Users } from "lucide-react";
import { Link } from "react-router-dom";

type Report = {
  id: string;
  slug: string;
  title: string;
  quarter_label: string | null;
  sector: string | null;
  county_scope: string | null;
  is_published: boolean;
  drafted_by_ai: boolean;
  page_count: number | null;
  storage_path: string | null;
  published_at: string | null;
  created_at: string;
};

type Lead = {
  id: string;
  email: string;
  name: string;
  org: string | null;
  role: string | null;
  source: string | null;
  consent_marketing: boolean;
  created_at: string;
  report_id: string | null;
};

export default function IntelReportsTab() {
  const [reports, setReports] = useState<Report[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: r, error: rErr }, { data: l, error: lErr }] = await Promise.all([
      supabase
        .from("intelligence_reports")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("intel_report_leads")
        .select("id, email, name, org, role, source, consent_marketing, created_at, report_id")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);
    if (rErr) toast.error(rErr.message);
    if (lErr) toast.error(lErr.message);
    setReports((r ?? []) as Report[]);
    setLeads((l ?? []) as Lead[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const draft = async (slug: string) => {
    setDrafting(slug);
    try {
      const { data, error } = await supabase.functions.invoke("draft-intel-report", {
        body: { slug },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("Report drafted & published");
      await load();
    } catch (e: any) {
      toast.error(e.message ?? "Draft failed");
    } finally {
      setDrafting(null);
    }
  };

  const download = async (path: string) => {
    const { data, error } = await supabase.storage.from("reports").createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      toast.error(error?.message ?? "Could not create download link");
      return;
    }
    window.open(data.signedUrl, "_blank");
  };

  const leadsByReport = leads.reduce<Record<string, number>>((acc, l) => {
    const k = l.report_id ?? "unassigned";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">Intelligence Reports</h2>
          <p className="text-sm text-muted-foreground">
            Draft, publish, and monitor quarterly reports and their captured leads.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Reports ({reports.length})
        </h3>
        {reports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reports yet.</p>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => {
              const leadCount = leadsByReport[r.id] ?? 0;
              return (
                <div
                  key={r.id}
                  className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-start gap-4"
                >
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          r.is_published
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {r.is_published ? "Published" : "Draft"}
                      </span>
                      {r.quarter_label && (
                        <span className="text-xs text-muted-foreground">{r.quarter_label}</span>
                      )}
                      {r.sector && (
                        <span className="text-xs text-muted-foreground">· {r.sector}</span>
                      )}
                      {r.county_scope && (
                        <span className="text-xs text-muted-foreground">· {r.county_scope}</span>
                      )}
                      {r.drafted_by_ai && (
                        <span className="text-xs text-muted-foreground">· AI-drafted</span>
                      )}
                    </div>
                    <h4 className="font-display font-semibold">{r.title}</h4>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                      <span className="font-mono">{r.slug}</span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" /> {leadCount} lead{leadCount === 1 ? "" : "s"}
                      </span>
                      {r.page_count != null && <span>{r.page_count} pp.</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/intelligence/reports/${r.slug}`} target="_blank">
                        <FileText className="h-4 w-4 mr-1" /> View
                      </Link>
                    </Button>
                    {r.storage_path && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => download(r.storage_path!)}
                      >
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => draft(r.slug)}
                      disabled={drafting === r.slug}
                    >
                      {drafting === r.slug ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      {r.storage_path ? "Re-draft" : "Draft & publish"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Captured leads ({leads.length})
        </h3>
        {leads.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leads captured yet.</p>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">Org</th>
                    <th className="text-left px-4 py-2">Role</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-left px-4 py-2">Report</th>
                    <th className="text-left px-4 py-2">Mkt</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => {
                    const rpt = reports.find((r) => r.id === l.report_id);
                    return (
                      <tr key={l.id} className="border-t border-border">
                        <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                          {new Date(l.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-2">{l.name}</td>
                        <td className="px-4 py-2 font-mono text-xs">{l.email}</td>
                        <td className="px-4 py-2">{l.org ?? "—"}</td>
                        <td className="px-4 py-2">{l.role ?? "—"}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {l.source ?? "—"}
                        </td>
                        <td className="px-4 py-2 text-xs">{rpt?.slug ?? "—"}</td>
                        <td className="px-4 py-2">
                          {l.consent_marketing ? (
                            <span className="text-xs text-primary">yes</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">no</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}